/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Real-time Streaming Message Feed & Smart Auto-Scroll (Task 10.3.3)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useChatStore, type ChatMessageItem } from '../../stores/useChatStore';
import { SYSTEM_CHAT_PERSONAS, CHAT_AI_MODELS } from '../../data/chat-personas.catalog';
import { getPersonaIcon } from './PersonaSelector';
import { MarkdownRenderer } from './MarkdownRenderer';
import { useI18n } from '../../hooks/useI18n';
import {
  User,
  Copy,
  Check,
  RotateCcw,
  Square,
  ArrowDown,
  Database,
  Coins,
  Cpu,
  ThumbsUp,
  ThumbsDown,
  Share2,
  ChevronDown,
  BookOpen,
} from 'lucide-react';

interface ChatMessageFeedProps {
  sessionId: string;
  onStopGenerating?: () => void;
  onRegenerate?: (message: ChatMessageItem, targetModelCode?: string) => void;
  className?: string;
}

export const ChatMessageFeed: React.FC<ChatMessageFeedProps> = ({
  sessionId,
  onStopGenerating,
  onRegenerate,
  className = '',
}) => {
  const { t, locale } = useI18n();
  const {
    getSessionMessages,
    isGenerating,
    sessions,
    rateMessage,
    openCitationDrawer,
    openShareModal,
  } = useChatStore();

  const messages = getSessionMessages(sessionId);
  const session = sessions.find((s) => s.id === sessionId);

  const persona = SYSTEM_CHAT_PERSONAS.find((p) => p.id === session?.personaId) || SYSTEM_CHAT_PERSONAS[0];
  const PersonaIcon = getPersonaIcon(persona.iconName);

  const containerRef = useRef<HTMLDivElement>(null);
  const bottomAnchorRef = useRef<HTMLDivElement>(null);

  const [isUserScrolledUp, setIsUserScrolledUp] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [feedbackPopoverMsgId, setFeedbackPopoverMsgId] = useState<string | null>(null);
  const [modelDropdownMsgId, setModelDropdownMsgId] = useState<string | null>(null);

  // Check scroll position to determine if user scrolled away from the bottom
  const handleScroll = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;

    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    // If distance is greater than 80px, user has scrolled up
    const isScrolledAway = distanceFromBottom > 80;
    setIsUserScrolledUp(isScrolledAway);
  }, []);

  // Smooth scroll to bottom
  const scrollToBottom = useCallback((smooth = true) => {
    if (bottomAnchorRef.current) {
      bottomAnchorRef.current.scrollIntoView({
        behavior: smooth ? 'smooth' : 'auto',
      });
      setIsUserScrolledUp(false);
    }
  }, []);

  // Auto-scroll when new messages or streaming tokens arrive (unless user scrolled up)
  useEffect(() => {
    if (!isUserScrolledUp) {
      scrollToBottom(false);
    }
  }, [messages, isUserScrolledUp, scrollToBottom]);

  const handleCopyMessage = async (msgId: string, content: string) => {
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard) {
        await navigator.clipboard.writeText(content);
        setCopiedId(msgId);
        setTimeout(() => setCopiedId(null), 2000);
      }
    } catch {
      // Ignored
    }
  };

  return (
    <div className={`relative flex flex-col h-full overflow-hidden ${className}`}>
      {/* Scrollable Message List */}
      <div
        ref={containerRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-6 scrollbar-thin scroll-smooth"
      >
        {/* Session Timestamp Pill */}
        {session && (
          <div className="flex justify-center my-2">
            <span className="text-[10px] uppercase tracking-wider font-mono px-3 py-1 rounded-full bg-surface-border/80 text-foreground-muted shadow-xs">
              {t('chat.sessionCreatedTime', {
                date: new Date(session.createdAt).toLocaleDateString(locale === 'vi' ? 'vi-VN' : 'en-US'),
                time: new Date(session.createdAt).toLocaleTimeString(locale === 'vi' ? 'vi-VN' : 'en-US', { hour: '2-digit', minute: '2-digit' }),
              })}
            </span>
          </div>
        )}

        {/* Message Items Feed */}
        {messages.map((message) => {
          const isUser = message.role === 'user';
          const isAssistant = message.role === 'assistant';

          return (
            <div
              key={message.id}
              className={`flex gap-3 sm:gap-4 max-w-3xl mx-auto ${
                isUser ? 'flex-row-reverse' : 'flex-row'
              } animate-in fade-in duration-200`}
            >
              {/* Avatar Icon */}
              <div
                className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm ${
                  isUser
                    ? 'bg-primary text-primary-fg'
                    : 'bg-gradient-to-tr from-primary/20 to-accent/20 border border-primary/30 text-primary'
                }`}
              >
                {isUser ? <User className="w-4 h-4" /> : <PersonaIcon className="w-4 h-4" />}
              </div>

              {/* Message Bubble & Content */}
              <div
                className={`flex flex-col space-y-1.5 max-w-[85%] sm:max-w-[78%] ${
                  isUser ? 'items-end' : 'items-start'
                }`}
              >
                {/* Sender Name & Meta */}
                <div className="flex items-center gap-2 px-1 text-[11px] text-foreground-muted">
                  <span className="font-semibold text-foreground">
                    {isUser ? t('chat.you') : persona.name}
                  </span>
                  {isAssistant && message.modelCode && (
                    <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.2 rounded-full bg-surface-border text-foreground-muted font-mono">
                      <Cpu className="w-3 h-3 text-primary" />
                      {message.modelCode}
                    </span>
                  )}
                  <span className="text-[10px]">
                    {new Date(message.createdAt).toLocaleTimeString(locale === 'vi' ? 'vi-VN' : 'en-US', {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                </div>

                {/* Message Body */}
                <div
                  className={`p-4 rounded-2xl shadow-sm text-xs leading-relaxed ${
                    isUser
                      ? 'rounded-tr-sm bg-primary text-primary-fg'
                      : 'rounded-tl-sm border border-surface-border bg-surface text-foreground'
                  }`}
                >
                  {isUser ? (
                    <div className="space-y-2">
                      <p className="whitespace-pre-wrap">{message.content}</p>

                      {/* Attachments rendering for User */}
                      {message.attachments && message.attachments.length > 0 && (
                        <div className="flex flex-wrap gap-2 pt-1 border-t border-primary-fg/20">
                          {message.attachments.map((att) => (
                            <div
                              key={att.id}
                              className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-black/20 text-[11px]"
                            >
                              {att.type === 'image' && att.previewUrl ? (
                                <img
                                  src={att.previewUrl}
                                  alt={att.name}
                                  className="w-4 h-4 rounded object-cover"
                                />
                              ) : (
                                <Database className="w-3 h-3 text-white/80" />
                              )}
                              <span className="truncate max-w-[120px]">{att.name}</span>
                              <span className="opacity-70 text-[10px]">({att.formattedSize})</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {/* Rich Markdown with Code & LaTeX */}
                      <MarkdownRenderer
                        content={message.content}
                        isStreaming={message.isStreaming}
                      />

                      {/* RAG Citations if present */}
                      {message.citations && message.citations.length > 0 && (
                        <div className="pt-2 border-t border-surface-border/60 space-y-1.5">
                          <div className="flex items-center justify-between">
                            <span className="text-[10px] font-bold uppercase tracking-wider text-accent flex items-center gap-1">
                              <Database className="w-3 h-3" />
                              {t('chat.ragSourcesHeading')}
                            </span>
                            <span className="text-[10px] text-foreground-muted">
                              {t('chat.clickToViewSource')}
                            </span>
                          </div>

                          <div className="flex flex-wrap gap-1.5">
                            {message.citations.map((c, cIdx) => (
                              <button
                                key={cIdx}
                                type="button"
                                onClick={() =>
                                  openCitationDrawer(message.id, cIdx, message.citations!)
                                }
                                className="group flex items-center gap-1.5 px-2.5 py-1 rounded-xl bg-accent/10 hover:bg-accent/20 border border-accent/20 hover:border-accent text-[11px] text-accent font-medium transition-all shadow-sm cursor-pointer max-w-[220px]"
                                title={t('chat.clickToViewSource')}
                              >
                                <BookOpen className="w-3 h-3 flex-shrink-0 group-hover:scale-110 transition-transform" />
                                <span className="truncate">
                                  {c.fileName || c.title || t('chat.citationNum', { num: cIdx + 1 })}
                                </span>
                                {c.score && (
                                  <span className="text-[9px] px-1.5 py-0.2 rounded-full bg-accent/20 font-bold font-mono text-accent">
                                    {Math.round(c.score * 100)}%
                                  </span>
                                )}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Assistant Message Actions Toolbar */}
                {isAssistant && !message.isStreaming && (
                  <div className="relative flex flex-wrap items-center gap-1 sm:gap-2 px-1 text-[11px] text-foreground-muted">
                    {/* 1. Copy Markdown */}
                    <button
                      type="button"
                      onClick={() => handleCopyMessage(message.id, message.content)}
                      className="flex items-center gap-1 hover:text-foreground transition-colors p-1.5 rounded-lg hover:bg-muted"
                      title={t('chat.copyMarkdownTooltip')}
                    >
                      {copiedId === message.id ? (
                        <>
                          <Check className="w-3.5 h-3.5 text-emerald-500" />
                          <span className="text-emerald-500 font-semibold text-[10px]">{t('common.copied')}</span>
                        </>
                      ) : (
                        <>
                          <Copy className="w-3.5 h-3.5" />
                          <span className="text-[10px]">{t('common.copy')}</span>
                        </>
                      )}
                    </button>

                    {/* 2. Regenerate with Model Switcher Popover */}
                    {onRegenerate && (
                      <div className="relative inline-flex items-center rounded-lg border border-surface-border bg-surface hover:bg-muted transition-colors">
                        <button
                          type="button"
                          onClick={() => onRegenerate(message)}
                          className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium hover:text-foreground"
                          title={t('chat.regenerateTooltip')}
                        >
                          <RotateCcw className="w-3 h-3" />
                          <span>{t('chat.regenerate')}</span>
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            setModelDropdownMsgId(
                              modelDropdownMsgId === message.id ? null : message.id
                            )
                          }
                          className="px-1 py-1 border-l border-surface-border hover:text-foreground"
                          title={t('chat.compareModelTooltip')}
                        >
                          <ChevronDown className="w-3 h-3" />
                        </button>

                        {/* Model Dropdown Menu */}
                        {modelDropdownMsgId === message.id && (
                          <div className="absolute top-full left-0 mt-1 w-44 rounded-xl border border-surface-border bg-surface p-1.5 shadow-xl z-30 animate-in fade-in zoom-in-95 space-y-1">
                            <span className="text-[9px] uppercase font-bold tracking-wider px-2 py-1 text-foreground-muted block">
                              {t('chat.changeModelRegenerate')}
                            </span>
                            {CHAT_AI_MODELS.map((m) => (
                              <button
                                key={m.code}
                                type="button"
                                onClick={() => {
                                  onRegenerate(message, m.code);
                                  setModelDropdownMsgId(null);
                                }}
                                className={`w-full flex items-center justify-between px-2 py-1.5 rounded-lg text-[11px] text-left transition-colors ${
                                  message.modelCode === m.code
                                    ? 'bg-primary/10 text-primary font-bold'
                                    : 'text-foreground hover:bg-muted'
                                }`}
                              >
                                <span className="truncate">{m.name}</span>
                                <span className="text-[9px] text-foreground-muted">
                                  {m.creditCost} {t('common.credits')}
                                </span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* 3. Feedback Thumbs Up / Down */}
                    <div className="flex items-center gap-0.5 border border-surface-border rounded-lg p-0.5 bg-surface">
                      <button
                        type="button"
                        onClick={() =>
                          rateMessage(
                            sessionId,
                            message.id,
                            message.feedback === 'like' ? null : 'like'
                          )
                        }
                        className={`p-1 rounded-md transition-colors ${
                          message.feedback === 'like'
                            ? 'text-emerald-500 bg-emerald-500/10'
                            : 'hover:text-foreground hover:bg-muted'
                        }`}
                        title={t('chat.helpfulAnswer')}
                      >
                        <ThumbsUp
                          className={`w-3.5 h-3.5 ${
                            message.feedback === 'like' ? 'fill-emerald-500' : ''
                          }`}
                        />
                      </button>

                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => {
                            if (message.feedback === 'dislike') {
                              rateMessage(sessionId, message.id, null);
                              setFeedbackPopoverMsgId(null);
                            } else {
                              rateMessage(sessionId, message.id, 'dislike');
                              setFeedbackPopoverMsgId(message.id);
                            }
                          }}
                          className={`p-1 rounded-md transition-colors ${
                            message.feedback === 'dislike'
                              ? 'text-rose-500 bg-rose-500/10'
                              : 'hover:text-foreground hover:bg-muted'
                          }`}
                          title={t('chat.unhelpfulAnswer')}
                        >
                          <ThumbsDown
                            className={`w-3.5 h-3.5 ${
                              message.feedback === 'dislike' ? 'fill-rose-500' : ''
                            }`}
                          />
                        </button>

                        {/* Dislike Reason Popover */}
                        {feedbackPopoverMsgId === message.id && (
                          <div className="absolute bottom-full left-0 mb-1 w-48 rounded-xl border border-surface-border bg-surface p-2 shadow-xl z-30 animate-in fade-in zoom-in-95 space-y-1">
                            <span className="text-[10px] font-bold text-foreground block">
                              {t('chat.dislikeReasonTitle')}
                            </span>
                            {[
                              { label: t('chat.dislikeInaccurate'), val: 'Inaccurate' },
                              { label: t('chat.dislikeLacksDetail'), val: 'Lacks detail' },
                              { label: t('chat.dislikeOffTopic'), val: 'Off-topic' },
                              { label: t('chat.dislikeOther'), val: 'Other' },
                            ].map((r) => (
                              <button
                                key={r.val}
                                type="button"
                                onClick={() => {
                                  rateMessage(sessionId, message.id, 'dislike', r.val);
                                  setFeedbackPopoverMsgId(null);
                                }}
                                className="w-full text-left px-2 py-1 rounded-md text-[10px] hover:bg-muted text-foreground-muted hover:text-foreground transition-colors"
                              >
                                {r.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* 4. Share Session Link Button */}
                    <button
                      type="button"
                      onClick={() => openShareModal(sessionId)}
                      className="flex items-center gap-1 hover:text-foreground transition-colors p-1.5 rounded-lg hover:bg-muted"
                      title={t('chat.shareChatTooltip')}
                    >
                      <Share2 className="w-3.5 h-3.5" />
                      <span className="text-[10px]">{t('chat.shareChat')}</span>
                    </button>

                    {/* 5. Tokens & Model Metas */}
                    {message.tokensUsed && (
                      <span className="flex items-center gap-1 text-[10px] opacity-80 font-mono ml-auto">
                        <Coins className="w-3 h-3 text-primary" />
                        {message.tokensUsed} tokens
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {/* Bottom anchor for scrolling */}
        <div ref={bottomAnchorRef} />
      </div>

      {/* Floating Controls: Stop Generating & Scroll To Bottom */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 z-20 pointer-events-none">
        {/* Stop Generating Button */}
        {isGenerating && (
          <button
            type="button"
            onClick={onStopGenerating}
            className="pointer-events-auto flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-surface-border bg-surface text-foreground hover:bg-muted text-xs font-semibold shadow-lg transition-all animate-bounce"
          >
            <Square className="w-3.5 h-3.5 text-destructive fill-destructive" />
            <span>{t('chat.stopGenerating')}</span>
          </button>
        )}

        {/* Floating Scroll To Bottom Button */}
        {isUserScrolledUp && (
          <button
            type="button"
            onClick={() => scrollToBottom(true)}
            className="pointer-events-auto flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary text-primary-fg hover:opacity-90 text-xs font-semibold shadow-lg transition-all active:scale-95"
            title={t('chat.scrollToBottom')}
          >
            <ArrowDown className="w-3.5 h-3.5 animate-bounce" />
            <span>{t('chat.scrollToBottom')}</span>
          </button>
        )}
      </div>
    </div>
  );
};
